import { useMemo, type ReactNode } from 'react'
import { CircleHelp } from 'lucide-react'

import {
  ListBrowseToolbar,
  type ListBrowseToolbarSearchProps,
} from '@/components/filters/list-browse-toolbar'
import type { FilterDefinition, ListFilterId, ListFilterLayout } from '@/components/filters/list-filter-types'
import { FILTER_HOVER_TEXT_FOREGROUND } from '@/components/filters/filter-styles'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import {
  excludeLeadFilterKeys,
  resolveFilterDefs,
} from '@/lib/list-filters/resolve-filter-defs'
import { resolveListFilterLayout } from '@/lib/list-filters/resolve-list-filter-layout'
import { cn } from '@/lib/utils'

const MAX_VISIBLE_FIELDS = 3

export type VisibleFilterFieldsSearchProps = ListBrowseToolbarSearchProps

export type VisibleFilterFieldsToolbarSlots = {
  viewSwitcher?: ReactNode
  sortMenu?: ReactNode
  trailingExtra?: ReactNode
}

export type VisibleFilterFieldsProps<TFilters, TCtx> = {
  listId: ListFilterId
  filters: TFilters
  onFiltersChange: (next: TFilters) => void
  definitions: FilterDefinition<TFilters, TCtx>[]
  ctx: TCtx
  /** Optional search row above the fields — does not invent filters (not Unified). */
  search?: VisibleFilterFieldsSearchProps
  layout?: ListFilterLayout
  /** Optional row between search and fields (e.g. thread deep-link chip). */
  betweenSlot?: ReactNode
  toolbar?: VisibleFilterFieldsToolbarSlots
  className?: string
  disabled?: boolean
}

/**
 * Casual-list filter paradigm: up to 3 always-visible labeled fields.
 * No secondary funnel / “Add filter” / “Clear all” — reset by changing each field.
 * Optional `search` renders ListBrowseToolbar above.
 * `layout.secondary` is ignored (invalid for this paradigm).
 */
export function VisibleFilterFields<TFilters, TCtx>({
  listId,
  filters,
  onFiltersChange,
  definitions,
  ctx,
  search,
  layout: layoutOverride,
  betweenSlot,
  toolbar,
  className,
  disabled,
}: VisibleFilterFieldsProps<TFilters, TCtx>) {
  const layout = useMemo(
    () =>
      layoutOverride ??
      resolveListFilterLayout(listId, { primary: definitions.map((def) => def.key), secondary: [] }),
    [layoutOverride, listId, definitions],
  )

  const definitionsByKey = useMemo(
    () => new Map(definitions.map((def) => [def.key, def])),
    [definitions],
  )

  const fieldDefs = useMemo(() => {
    // Secondary keys are not rendered — Visible Fields has no funnel.
    const keys = excludeLeadFilterKeys(layout.primary, layout.lead).slice(0, MAX_VISIBLE_FIELDS)
    return resolveFilterDefs(keys, definitionsByKey, ctx)
  }, [layout.primary, layout.lead, definitionsByKey, ctx])

  const popoverProps = useMemo(
    () => ({ filters, onChange: onFiltersChange, ctx }),
    [filters, onFiltersChange, ctx],
  )

  const showToolbar = Boolean(toolbar?.viewSwitcher || toolbar?.sortMenu || toolbar?.trailingExtra)

  return (
    <div className={cn('space-y-2', className)}>
      {search ? (
        <ListBrowseToolbar
          {...search}
          viewSwitcher={toolbar?.viewSwitcher}
          trailingActions={
            toolbar?.sortMenu || toolbar?.trailingExtra ? (
              <>
                {toolbar.sortMenu}
                {toolbar.trailingExtra}
              </>
            ) : undefined
          }
        />
      ) : null}

      {betweenSlot}

      <div className="flex flex-wrap items-end gap-3">
        {fieldDefs.map((def) => {
          const field = def.renderField?.(popoverProps)
          if (field == null) {
            throw new Error(
              `VisibleFilterFields: definition "${def.key}" on list "${listId}" is missing renderField`,
            )
          }
          return (
            <div
              key={def.key}
              className={cn(
                'flex min-w-[10rem] max-w-xs flex-1 flex-col gap-1.5',
                disabled && 'pointer-events-none opacity-50',
              )}
            >
              <span className="inline-flex items-center gap-1 text-sm font-medium text-foreground">
                {def.label}
                {def.fieldTooltip ? (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button
                        type="button"
                        className={cn(
                          'inline-flex size-4 shrink-0 items-center justify-center rounded-sm text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring',
                          'transition-[color,transform] duration-150 ease-[cubic-bezier(0.23,1,0.32,1)] motion-reduce:transition-none',
                          'active:scale-[0.97] motion-reduce:active:scale-100',
                          FILTER_HOVER_TEXT_FOREGROUND,
                        )}
                        aria-label={`${def.label} help`}
                      >
                        <CircleHelp className="size-3.5" aria-hidden />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent side="top" className="max-w-xs">
                      {def.fieldTooltip}
                    </TooltipContent>
                  </Tooltip>
                ) : null}
              </span>
              {field}
            </div>
          )
        })}

        {!search && showToolbar ? (
          <div className="ml-auto flex shrink-0 items-center gap-1 self-end">
            {toolbar?.viewSwitcher}
            {toolbar?.sortMenu}
            {toolbar?.trailingExtra}
          </div>
        ) : null}
      </div>
    </div>
  )
}
